import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUnitHistory } from '../../platform/engine/identitySpine';
import { getStageLabel } from '../../platform/store/useOperationalStore';

function fmtVisitDate(ts) {
  if (!ts) return '—';
  const ms = ts < 10_000_000_000 ? ts * 1000 : ts;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function UnitPanel({ truck }) {
  const navigate = useNavigate();
  const [history, setHistory] = useState(null); // null = loading/none

  // The identity-spine payoff: one VIN shows all its visits. Only jobs
  // linked to a Unit record have history (the backfill covered everything).
  useEffect(() => {
    let alive = true;
    if (!truck.unitId) { setHistory(null); return; }
    fetchUnitHistory(truck.unitId).then(jobs => {
      if (alive) setHistory(jobs.filter(j => j.id !== truck.id));
    });
    return () => { alive = false; };
  }, [truck.unitId, truck.id]);

  return (
    <>
      <dl className="ro-kv ro-kv--tab">
        {truck.year  && <><dt>Year</dt><dd>{truck.year}</dd></>}
        {truck.make  && <><dt>Make</dt><dd>{truck.make}</dd></>}
        {truck.model && <><dt>Model</dt><dd>{truck.model}</dd></>}
        {truck.vin   && <><dt>VIN</dt><dd className="ro-vin">{truck.vin}</dd></>}
        {truck.mileage && <><dt>Mileage</dt><dd>{truck.mileage}</dd></>}
        {truck.hours && <><dt>Hours</dt><dd>{truck.hours}</dd></>}
        {truck.faults && <><dt>Faults</dt><dd>{truck.faults}</dd></>}
      </dl>

      {history?.length > 0 && (
        <div className="ro-unit-history">
          <div className="ro-unit-history-title">
            Service history — {history.length} previous visit{history.length !== 1 ? 's' : ''}
          </div>
          {history.map(j => (
            <button key={j.id} type="button" className="ro-unit-visit" onClick={() => navigate(`/ro/${j.id}`)}>
              <span className="ro-unit-visit-ro">{j.repairOrder}</span>
              <span className="ro-unit-visit-date">{fmtVisitDate(j.startTime)}</span>
              <span className="ro-unit-visit-reason">{j.reason || '—'}</span>
              <span className="ro-unit-visit-status">{getStageLabel(j.status)}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

export function CustomerPanel({ truck, canEdit, poRequired, onSavePO }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [po, setPo] = useState(truck.poNumber || '');

  // The PO usually arrives by phone after the truck is already in the yard, so
  // it has to be editable here, not only at intake.
  async function save() {
    await onSavePO((po || '').trim() || null);
    setEditing(false);
  }

  return (
    <>
      <dl className="ro-kv ro-kv--tab">
        <dt>Company</dt><dd>{truck.companyName}</dd>
        <dt>PO #</dt>
        <dd>
          {editing ? (
            <span className="ro-po-edit">
              <input
                className="ro-po-input"
                value={po}
                autoFocus
                placeholder="Their PO number"
                onChange={e => setPo(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter')  save();
                  if (e.key === 'Escape') { setPo(truck.poNumber || ''); setEditing(false); }
                }}
              />
              <button type="button" className="ro-btn ro-btn--sm" onClick={save}>Save</button>
            </span>
          ) : (
            <span className={!truck.poNumber && poRequired ? 'ro-po-missing' : undefined}>
              {truck.poNumber || (poRequired ? 'Required — none on file' : '—')}
              {canEdit && onSavePO && (
                <button type="button" className="ro-po-btn" onClick={() => setEditing(true)}>
                  {truck.poNumber ? 'Change' : 'Add'}
                </button>
              )}
            </span>
          )}
        </dd>
        {truck.contactPerson && <><dt>Contact</dt><dd>{truck.contactPerson}</dd></>}
        {truck.phone && <><dt>Phone</dt><dd><a href={`tel:${truck.phone}`}>{truck.phone}</a></dd></>}
        {truck.email && <><dt>Email</dt><dd><a href={`mailto:${truck.email}`}>{truck.email}</a></dd></>}
        {truck.customerType && <><dt>Type</dt><dd>{truck.customerType}</dd></>}
        {truck.warranty && <><dt>Warranty</dt><dd>Yes</dd></>}
        {truck.partsHandling && <><dt>Parts</dt><dd>{truck.partsHandling}</dd></>}
      </dl>
      <button
        type="button"
        className="ro-btn ro-btn--ghost ro-btn--sm"
        onClick={() => navigate(`/customers/${encodeURIComponent(truck.customerId || truck.companyName)}`)}
      >
        Customer page →
      </button>
    </>
  );
}
